/**
 * @file Negative Impact Categories Service
 * @description API endpoints for negative impact categories admin operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { NegativeImpactCategory } from '@medsuite/models';

/**
 * Create negative impact category DTO
 */
export interface CreateNegativeImpactCategoryDto {
  name: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Update negative impact category DTO
 */
export interface UpdateNegativeImpactCategoryDto {
  name?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Category with usage info response
 */
export interface CategoryWithUsageResponse {
  category: NegativeImpactCategory;
  in_use: boolean;
}

/**
 * Negative Impact Categories Service
 */
class NegativeImpactCategoriesService {
  /**
   * Get all categories
   */
  async getAll(): Promise<ApiResponse<NegativeImpactCategory[]>> {
    return apiClient.get<NegativeImpactCategory[]>('/admin/negative-impact-categories');
  }

  /**
   * Get only active categories (for dropdowns)
   */
  async getActive(): Promise<ApiResponse<NegativeImpactCategory[]>> {
    return apiClient.get<NegativeImpactCategory[]>('/admin/negative-impact-categories/active');
  }

  /**
   * Get a single category by ID
   */
  async getById(id: number): Promise<ApiResponse<CategoryWithUsageResponse>> {
    return apiClient.get<CategoryWithUsageResponse>(`/admin/negative-impact-categories/${id}`);
  }

  /**
   * Create a new category
   */
  async create(
    data: CreateNegativeImpactCategoryDto
  ): Promise<ApiResponse<NegativeImpactCategory>> {
    return apiClient.post<NegativeImpactCategory>('/admin/negative-impact-categories', data);
  }

  /**
   * Update a category
   */
  async update(
    id: number,
    data: UpdateNegativeImpactCategoryDto
  ): Promise<ApiResponse<NegativeImpactCategory>> {
    return apiClient.put<NegativeImpactCategory>(`/admin/negative-impact-categories/${id}`, data);
  }

  /**
   * Delete (deactivate) a category
   */
  async delete(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/admin/negative-impact-categories/${id}`);
  }
}

export const negativeImpactCategoriesService = new NegativeImpactCategoriesService();
